import { Sample } from '../../@types/samples/types';
import {
  buildGasOilRefinementCurves,
  buildWaterOilRefinementCurves,
} from '../../components/utils/curveGeneration';

export type KrPoint = { x: number; y: number };

export interface KrChartData {
  /** X axis label: Sw (water-oil) or Sg (gas-oil). */
  xLabel: string;
  /** Decreasing curve label (always Kro). */
  decreasingLabel: string;
  /** Increasing curve label: Krw (water-oil) or Krg (gas-oil). */
  increasingLabel: string;
  measuredDecreasing: KrPoint[];
  measuredIncreasing: KrPoint[];
  curveDecreasing: KrPoint[];
  curveIncreasing: KrPoint[];
  /** True when the curves use the refined (true_*) parameter set. */
  refined: boolean;
}

const collect = (
  xArr?: (number | null)[] | null,
  yArr?: (number | null)[] | null
): KrPoint[] => {
  const points: KrPoint[] = [];
  (xArr ?? []).forEach((x, index) => {
    const y = yArr?.[index];
    if (x != null && y != null) points.push({ x, y });
  });
  return points;
};

const toPoints = (xs: number[], ys: number[]): KrPoint[] =>
  xs.map((x, index) => ({ x, y: ys[index] }));

/**
 * Chart data for a Kr methodology: the lab points plus the smooth Corey
 * curves reconstructed from the refined (true_*) parameters when the
 * auto/manual refinement populated them, falling back to the lab parameter
 * set. Reuses the same curve builders as the Kr Analysis Refinement Curves
 * tab so the batch preview matches the single-sample page exactly.
 */
export const getKrChartData = (
  sample: Sample,
  method: 'water-oil' | 'gas-oil',
  numberOfPoints = 30
): KrChartData | null => {
  const swi = sample.input_sample_values?.swi;

  if (method === 'water-oil') {
    const lab = sample.input_sample_curves?.water_oil;
    const values = sample.k_rel_values?.water_oil;
    const measuredDecreasing = collect(lab?.sw_krel, lab?.kro_lab);
    const measuredIncreasing = collect(lab?.sw_krel, lab?.krw_lab);

    const refined =
      values?.true_sor_w != null &&
      values.true_no_w != null &&
      values.true_nw != null &&
      values.true_krw != null;
    const sor = values?.true_sor_w ?? values?.sor_w;
    const no = values?.true_no_w ?? values?.no_w;
    const nw = values?.true_nw ?? values?.nw;
    const krwEndpoint = values?.true_krw ?? values?.krw_exponent;

    const curves = buildWaterOilRefinementCurves({
      swi: swi as number,
      sor: sor as number,
      no: no as number,
      nw: nw as number,
      krwEndpoint: krwEndpoint as number,
      numberOfPoints,
    });

    if (
      measuredDecreasing.length === 0 &&
      measuredIncreasing.length === 0 &&
      curves.sw.length === 0
    ) {
      return null;
    }

    return {
      xLabel: 'Sw',
      decreasingLabel: 'Kro',
      increasingLabel: 'Krw',
      measuredDecreasing,
      measuredIncreasing,
      curveDecreasing: toPoints(curves.sw, curves.kro),
      curveIncreasing: toPoints(curves.sw, curves.krw),
      refined,
    };
  }

  const lab = sample.input_sample_curves?.gas_oil;
  const values = sample.k_rel_values?.gas_oil;
  const measuredDecreasing = collect(lab?.sg_krel, lab?.kro_lab);
  const measuredIncreasing = collect(lab?.sg_krel, lab?.krg_lab);

  const refined =
    values?.true_sor_g != null &&
    values.true_no_g != null &&
    values.true_ng != null &&
    values.true_krg != null;
  const sor = values?.true_sor_g ?? values?.sor_g;
  const no = values?.true_no_g ?? values?.no_g;
  const ng = values?.true_ng ?? values?.ng;
  const krgEndpoint = values?.true_krg ?? values?.krg_exponent;
  // The refined Corey exponents/endpoint are fit on the critical
  // residual Sorc (the gas `sor` field, ~0.05); reproduce them by
  // normalizing on Sorc while the curve still spans the Sg range up
  // to the material-balance Sgmax (= 1 - swi - true_sor_g). The lab
  // fallback parameters are fit on the Sorg axis, so they normalize
  // on `sor` as before.
  const normSor = refined ? values?.sor ?? 0.05 : sor;

  const curves = buildGasOilRefinementCurves({
    swi: swi as number,
    sgc: values?.sgc ?? 0,
    sor: sor as number,
    no: no as number,
    ng: ng as number,
    krgEndpoint: krgEndpoint as number,
    normSor: normSor as number,
    numberOfPoints,
  });

  if (
    measuredDecreasing.length === 0 &&
    measuredIncreasing.length === 0 &&
    curves.sg.length === 0
  ) {
    return null;
  }

  return {
    xLabel: 'Sg',
    decreasingLabel: 'Kro',
    increasingLabel: 'Krg',
    measuredDecreasing,
    measuredIncreasing,
    curveDecreasing: toPoints(curves.sg, curves.kro),
    curveIncreasing: toPoints(curves.sg, curves.krg),
    refined,
  };
};
